import { useEffect, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { usePageTitle } from '../hooks/usePageTitle';
import { TabMenu, type TabItem } from '@themes/aegix';
import { SMSPageHeader as PageHeader } from '../components/layout/SMSPageHeader';
import {
  HiShieldCheck,
  HiAcademicCap,
  HiLink,
  HiDocumentDuplicate,
} from 'react-icons/hi2';
import { FaFileCircleCheck } from 'react-icons/fa6';
import { MdOutlinePayment } from 'react-icons/md';
import {
  AuthorizationContent,
  DocumentsContent,
  BillingContent,
  IntegrationsContent,
  TemplatesContent,
} from '../components/admin';
import { OnboardingContent } from '../components/admin/OnboardingContent';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFeature, useFeaturesLoading } from '../hooks/useFeature';
import { FeatureCode } from '../types/features';
import { useHasPermissions } from '../utils/hasPermission';

const DEFAULT_TAB = 'people';

interface AccountTab extends TabItem {
  subtitle: string;
}

const allTabs: AccountTab[] = [
  {
    value: 'people',
    label: 'People',
    icon: HiShieldCheck,
    subtitle: 'Manage Users, Roles and Permissions',
    'data-e2e': 'tab-directory',
  },
  {
    value: 'integrations',
    label: 'Integrations',
    icon: HiLink,
    subtitle: 'Manage integrated tools and apps',
    'data-e2e': 'tab-integrations',
  },
  {
    value: 'documents',
    label: 'Documents',
    icon: FaFileCircleCheck,
    subtitle: 'View Organization Documents',
    'data-e2e': 'tab-documents',
  },
  {
    value: 'onboarding',
    label: 'Onboarding',
    icon: HiAcademicCap,
    subtitle: 'Track User Onboarding Progress',
  },
  {
    value: 'billing',
    label: 'Billing',
    icon: MdOutlinePayment,
    subtitle: 'Manage Billing and Payments',
    'data-e2e': 'tab-billing',
  },
  {
    value: 'templates',
    label: 'Templates',
    icon: HiDocumentDuplicate,
    subtitle: 'License templates applied at vendor-driven onboarding (ADR 104 §18)',
    'data-e2e': 'tab-templates',
  },
];

const tabContent: Record<string, React.FC<{ initialSubTab?: string }>> = {
  people: AuthorizationContent,
  integrations: IntegrationsContent,
  documents: DocumentsContent,
  onboarding: OnboardingContent,
  billing: BillingContent,
  templates: TemplatesContent,
};

// Legacy tab values redirected to renamed in-page equivalents.
const IN_PAGE_REDIRECTS: Record<string, string> = {
  directory: 'people',
};

// Legacy tab values that belong on a feature page. Per ADR-114 Phase 2,
// per-feature configuration lives in Host / Reunify themselves, not in
// Account. Hitting `/account?tab=host` or `?tab=reunification` is
// caught here and forwarded to the feature page's `Configure` tab.
const EXTERNAL_REDIRECTS: Record<string, string> = {
  host: '/visitor-mgmt?tab=configure',
  'visitor-mgmt': '/visitor-mgmt?tab=configure',
  reunification: '/reunification?tab=configure',
};

import { useTranslation } from 'react-i18next';

export function Account() {
  const { t } = useTranslation();
  usePageTitle(t('account.title', 'Account'));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const documentsEnabled = useFeature(FeatureCode.ACCOUNT_DOCUMENTS);
  const billingEnabled = useFeature(FeatureCode.ACCOUNT_BILLING);
  const featuresLoading = useFeaturesLoading();
  const canManageLicense = useHasPermissions(['licensing.manage_license']);

  const tabEnabled = useMemo<Record<string, boolean>>(
    () => ({
      documents: documentsEnabled,
      billing: billingEnabled,
      templates: canManageLicense,
    }),
    [documentsEnabled, billingEnabled, canManageLicense],
  );

  const visibleTabs = useMemo(
    () =>
      allTabs
        .filter((tab) => tabEnabled[tab.value] !== false)
        .map((tab) => ({
          ...tab,
          label: t(`account.tabs.${tab.value}.label`, tab.label),
          subtitle: t(`account.tabs.${tab.value}.subtitle`, tab.subtitle),
        })),
    [tabEnabled, t],
  );

  const rawTab = searchParams.get('tab');

  // Same-page redirect (e.g. `directory` → `people`).
  useEffect(() => {
    if (rawTab && IN_PAGE_REDIRECTS[rawTab]) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', IN_PAGE_REDIRECTS[rawTab]);
          return next;
        },
        { replace: true },
      );
    }
  }, [rawTab, setSearchParams]);

  // External redirect (feature-local Configure surface, Phase 2).
  // Preserves the inbound `subtab` query if present.
  useEffect(() => {
    if (rawTab && EXTERNAL_REDIRECTS[rawTab]) {
      const target = EXTERNAL_REDIRECTS[rawTab];
      const subtab = searchParams.get('subtab');
      const url = subtab ? `${target}&subtab=${subtab}` : target;
      navigate(url, { replace: true });
    }
  }, [rawTab, searchParams, navigate]);

  // Flag-gated redirect: a tab whose flag is OFF falls back to the default
  // tab instead of rendering an empty page when deep-linked. Wait for the
  // feature query to settle so a user with the flag ON isn't bounced during
  // the initial load.
  useEffect(() => {
    if (featuresLoading) return;
    if (rawTab && tabEnabled[rawTab] === false) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', DEFAULT_TAB);
          next.delete('subtab');
          next.delete('id');
          next.delete('public_id');
          return next;
        },
        { replace: true },
      );
    }
  }, [featuresLoading, rawTab, tabEnabled, setSearchParams]);

  const activeTab = rawTab || DEFAULT_TAB;
  const activeSubTab = searchParams.get('subtab') || undefined;

  // Don't render a disabled tab's content even before the redirect effect runs.
  const isActiveTabEnabled = tabEnabled[activeTab] !== false;
  const renderedTab = isActiveTabEnabled ? activeTab : DEFAULT_TAB;

  const activeTabData = visibleTabs.find((t) => t.value === renderedTab);

  const ActiveContent = tabContent[renderedTab];

  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      next.delete('subtab');
      next.delete('id');
      next.delete('public_id');
      return next;
    });
  };

  return (
    <Box mx={-8} mt={-8}>
      <PageHeader
        icon={activeTabData?.icon as React.ElementType}
        title={activeTabData?.label || t('account.tabs.people.label', 'People')}
        subtitle={activeTabData?.subtitle || ''}
        showSearch={true}
        showNotifications={true}
      />
      <TabMenu tabs={visibleTabs} activeTab={renderedTab} onChange={handleTabChange} e2eTitle="AccountTabs" />

      {ActiveContent && (
        <ActiveContent key={activeSubTab} initialSubTab={activeSubTab} />
      )}
    </Box>
  );
}
